import { GuestStripeProvider } from "./add-element/website-display/GuestStripeContext";
import { CustomColorThemesProvider } from "./contexts/CustomColorThemesContext";
import { DeepLinkContextProvider } from "./contexts/DeepLinkContext";
import { ModelContexts } from "./contexts/ModelContexts";
import { TrackContextProvider } from "./contexts/TrackContext";
import { VisibleGuestListProvider } from "./contexts/VisibleGuestListContext";
import API from "./core/api";
import { deepCamelCaseKeys } from "./core/api/typed/drf";
import { SentryBoundary } from "./core/components/SentryBoundary";
import { trackPageLoad } from "./core/instrumentation";
import { createRoutes, unauthenticatedRoutes } from "./core/routing";
import { RouterSetup } from "./core/routing";
import configureStore from "./core/store";
import { loadSiteData } from "./website/actions";
import LoginWrapper from "./website/components/LoginWrapper";
import WeddingWebsite from "./website/components/WeddingWebsite";
import OnlineInvitationSite from "./website/components/online-invitation/OnlineInvitationSite";
import { filterPage } from "./website/selectors";
import config from "config";
import React from "react";
import { createRoot } from "react-dom/client";
import ReactGA from "react-ga";
import { Provider } from "react-redux";
import smoothScroll from "smoothscroll-polyfill";
import { v4 as uuidV4 } from "uuid";

smoothScroll.polyfill();

ReactGA.initialize(config.get("guestGATrackingID"));

const siteData = deepCamelCaseKeys(window.__DATA__, ["themeDataOverrides"]);

const {
  accommodations,
  attendants,
  comments,
  customColorThemes,
  events,
  isStripeOnboardingSubmitted,
  imageUploads,
  isStripeSetupComplete,
  pages,
  photos,
  registries,
  videos,
  ...rest
} = siteData;

// Adding empty site details/feature flags in case the site is password protected
// and the backend isn't including them yet
const site = {
  ...rest,
  siteDetails: rest.siteDetails ?? {},
  featureFlags: rest.featureFlags ?? {},
};

const data = {
  accommodations,
  attendants,
  comments,
  events,
  "image-uploads": imageUploads,
  pages,
  photos,
  registries,
  videos,
};

const baseUrl = window.__BASE_URL__;
const guestAuthenticated = window.__GUEST_AUTHENTICATED__ === "True";

let routes;

if (guestAuthenticated) {
  routes = createRoutes(
    pages.filter((page) => filterPage(page, { ...data, sites: [site] })),
  );
} else {
  routes = unauthenticatedRoutes;
}

const deepLinkContactId = window.__ADDRESSBOOK_CONTACT_ID__;

API.setDomain(siteData.domain);

const store = configureStore();

store.dispatch(loadSiteData(data));

let SiteComponent;

if (site.isWedding) {
  SiteComponent = WeddingWebsite;
} else {
  SiteComponent = OnlineInvitationSite;
}

store.dispatch({
  payload: window.__THEME_DATA__,
  type: "FETCH_THEME_FULFILLED",
});

let guestId = localStorage.getItem("mintedWebsitesGuestId");

if (!guestId) {
  guestId = uuidV4();

  localStorage.setItem("mintedWebsitesGuestId", guestId);
}

trackPageLoad(
  site.isDemoSite ? "site_demo" : "site_guest_experience",
  site,
  guestId,
);

createRoot(document.getElementById("root")).render(
  <SentryBoundary>
    <ModelContexts initialSites={[site]}>
      <Provider store={store}>
        <RouterSetup basename={baseUrl} routes={routes} syncWithRedux={true}>
          <VisibleGuestListProvider>
            <TrackContextProvider site={site}>
              <CustomColorThemesProvider
                initialCustomColorThemes={customColorThemes || []}
              >
                <LoginWrapper>
                  <DeepLinkContextProvider
                    addressbookContactId={deepLinkContactId}
                  >
                    <GuestStripeProvider
                      isStripeOnboardingSubmitted={Boolean(
                        isStripeOnboardingSubmitted,
                      )}
                      isStripeSetupComplete={Boolean(isStripeSetupComplete)}
                    >
                      <SiteComponent />
                    </GuestStripeProvider>
                  </DeepLinkContextProvider>
                </LoginWrapper>
              </CustomColorThemesProvider>
            </TrackContextProvider>
          </VisibleGuestListProvider>
        </RouterSetup>
      </Provider>
    </ModelContexts>
  </SentryBoundary>,
);
